import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const AdminSettingsTab = () => {
  const [editDeadline, setEditDeadline] = useState('');
  const [passed, setPassed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await ax.get(`${API}/admin/submission-settings`);
        setEditDeadline(data?.edit_deadline || '');
        setPassed(!!data?.edit_deadline_passed);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load settings.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(editDeadline)) {
      toast.error('Please enter a valid date (YYYY-MM-DD).');
      return;
    }
    setSaving(true);
    try {
      const { data } = await ax.put(`${API}/admin/submission-settings`, { edit_deadline: editDeadline });
      setEditDeadline(data?.edit_deadline || editDeadline);
      toast.success('Settings updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div>;

  return (
    <div data-testid="admin-settings-tab" className="max-w-xl">
      <p className="text-[var(--ink-muted)] text-sm mb-6">Global platform settings for AdAura Awards 2026.</p>

      <div className="border border-[var(--gold-faint)] bg-white p-6">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Submission Edit Deadline</div>
        <h3 className="font-serif text-lg text-[var(--ink)] mt-1">Editing closes on</h3>
        <p className="text-[12.5px] text-[var(--ink-muted)] mt-2 leading-relaxed">
          Nominators can edit their submitted entries until end-of-day (IST) on this date. After the deadline, submitted entries are automatically locked.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <input type="date" value={editDeadline} onChange={(e) => setEditDeadline(e.target.value)}
            data-testid="settings-edit-deadline"
            className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm"
            style={{ colorScheme: 'light' }} />
          <button onClick={save} disabled={saving} data-testid="settings-save"
            className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            <Save size={13} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {passed && (
          <p className="mt-4 text-[11.5px] text-[#C2613A]" data-testid="settings-deadline-passed">
            ⚠ The deadline has already passed. Nominators can no longer edit submitted entries.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminSettingsTab;
